import { logger } from "./Logger";
import { estimateTokenCount } from "./TokenCounter";

interface AIWorkflowLogEntry {
  type: string;
  timestamp: string;
  data: any;
  step?: string;
  phase?: string;
}

interface AnalysisMetrics {
  tokenEfficiency: number;
  responseQuality: number;
  fileSelectionAccuracy: number;
  processingTime: number;
  errorRate: number;
  recommendations: string[];
}

interface ParsedContent {
  fieldName: string;
  rawContent: string;
  parsedContent: any;
  parseSuccessful: boolean;
  parseError?: string;
}

class AIWorkflowLogger {
  private logs: AIWorkflowLogEntry[] = [];
  private rawLogs: string[] = [];
  private startTime: number = 0;
  private currentWorkflowId: string = "";

  initializeWorkflow(workflowId: string = `workflow_${Date.now()}`): void {
    this.currentWorkflowId = workflowId;
    this.startTime = Date.now();
    this.logs = [];
    this.rawLogs = [];

    // Initialize global window object for browser access
    if (typeof window !== "undefined") {
      window.aiWorkflowLogs = [];
      window.aiWorkflowRawLogs = [];
      window.aiWorkflowAnalysis = null;
    }

    const rawLogEntry = `DEBUGGING_AI_QUALITY: Workflow Initialized\n${JSON.stringify(
      {
        workflowId: this.currentWorkflowId,
        timestamp: new Date().toISOString(),
      },
      null,
      2
    )}\n${"=".repeat(80)}\n`;

    this.rawLogs.push(rawLogEntry);
    if (typeof window !== "undefined") {
      window.aiWorkflowRawLogs = window.aiWorkflowRawLogs || [];
      window.aiWorkflowRawLogs.push(rawLogEntry);
    }

    logger.debug.info("DEBUGGING_AI_QUALITY: Workflow Initialized", {
      workflowId: this.currentWorkflowId,
      timestamp: new Date().toISOString(),
    });
  }

  logStep(type: string, data: any, step?: string, phase?: string): void {
    const logEntry: AIWorkflowLogEntry = {
      type,
      timestamp: new Date().toISOString(),
      data: { ...data },
      step,
      phase,
    };

    this.logs.push(logEntry);

    // Create raw log entry that matches console output format
    const rawLogEntry = `DEBUGGING_AI_QUALITY: ${type}\n${JSON.stringify(
      data,
      null,
      2
    )}\n${"=".repeat(80)}\n`;
    this.rawLogs.push(rawLogEntry);

    // Store in global window for browser access
    if (typeof window !== "undefined") {
      window.aiWorkflowLogs = window.aiWorkflowLogs || [];
      window.aiWorkflowLogs.push(logEntry);
      window.aiWorkflowRawLogs = window.aiWorkflowRawLogs || [];
      window.aiWorkflowRawLogs.push(rawLogEntry);
    }

    logger.debug.info(`DEBUGGING_AI_QUALITY: ${type}`, data);
  }

  // Helper method to detect and parse stringified JSON
  private tryParseJsonString(value: any): ParsedContent | null {
    if (typeof value !== "string") return null;

    // Skip very short strings that are unlikely to be JSON
    if (value.length < 10) return null;

    // Skip strings that don't start with JSON-like characters
    const trimmed = value.trim();
    if (
      !trimmed.startsWith("{") &&
      !trimmed.startsWith("[") &&
      !trimmed.startsWith('"')
    ) {
      return null;
    }

    try {
      // First attempt: direct parsing
      let parsed = JSON.parse(value);

      // If the result is still a string, try parsing again (double stringification)
      if (typeof parsed === "string") {
        const secondParsed = JSON.parse(parsed);
        return {
          fieldName: "",
          rawContent: value,
          parsedContent: secondParsed,
          parseSuccessful: true,
        };
      }

      return {
        fieldName: "",
        rawContent: value,
        parsedContent: parsed,
        parseSuccessful: true,
      };
    } catch (error) {
      return {
        fieldName: "",
        rawContent: value,
        parsedContent: null,
        parseSuccessful: false,
        parseError:
          error instanceof Error ? error.message : "Unknown parsing error",
      };
    }
  }

  // Helper method to find all stringified JSON in an object
  private findStringifiedJson(obj: any, path: string = ""): ParsedContent[] {
    const results: ParsedContent[] = [];

    if (obj === null || obj === undefined) return results;

    if (typeof obj === "string") {
      const parseResult = this.tryParseJsonString(obj);
      if (parseResult) {
        parseResult.fieldName = path;
        results.push(parseResult);
      }
    } else if (typeof obj === "object" && !Array.isArray(obj)) {
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = path ? `${path}.${key}` : key;
        results.push(...this.findStringifiedJson(value, currentPath));
      }
    } else if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const currentPath = `${path}[${index}]`;
        results.push(...this.findStringifiedJson(item, currentPath));
      });
    }

    return results;
  }

  // Helper method to create a nicely formatted display of content
  private formatContent(content: any, maxLength?: number): string {
    if (content === null || content === undefined) {
      return "null";
    }

    let formatted: string;

    if (typeof content === "string") {
      formatted = content;
    } else {
      formatted = JSON.stringify(content, null, 2);
    }

    // If maxLength is specified and content is longer, show truncated version with note
    if (maxLength && formatted.length > maxLength) {
      return (
        formatted.substring(0, maxLength) +
        `\n\n... [Content truncated. Total length: ${formatted.length} characters]`
      );
    }

    return formatted;
  }

  private analyzeStep1(): AnalysisMetrics {
    const step1Logs = this.logs.filter((log) => log.step === "step1");

    return {
      tokenEfficiency: 100, // Step 1 doesn't use tokens
      responseQuality: 100, // Always successful if completed
      fileSelectionAccuracy: 100, // Repository tree generation is deterministic
      processingTime: this.calculateStepDuration("step1"),
      errorRate: 0,
      recommendations: [
        "Repository tree generation completed successfully",
        "Consider optimizing indexing if duration > 5000ms",
      ],
    };
  }

  private analyzeStep2(): AnalysisMetrics {
    const step2Logs = this.logs.filter((log) => log.step === "step2");
    const inputLog = step2Logs.find((log) => log.type.includes("Input"));
    const responseLog = step2Logs.find((log) => log.type.includes("Response"));
    const parsedLog = step2Logs.find((log) => log.type.includes("Parsed"));

    const recommendations: string[] = [];
    let tokenEfficiency = 50;
    let responseQuality = 50;
    let fileSelectionAccuracy = 50;

    if (inputLog?.data) {
      const promptTokens = inputLog.data.promptTokens || 0;
      const maxTokens = 100000; // Reasonable limit
      tokenEfficiency = Math.max(0, 100 - (promptTokens / maxTokens) * 100);

      if (promptTokens > 80000) {
        recommendations.push(
          "⚠️ HIGH TOKEN USAGE: Prompt exceeds 80k tokens, consider reducing repo tree depth"
        );
      }
    }

    if (responseLog?.data && parsedLog?.data) {
      const responseTokens = responseLog.data.responseTokens || 0;
      const filesSelected = parsedLog.data.totalFilesSuggested || 0;

      responseQuality = Math.min(100, (responseTokens / 1000) * 10); // Quality based on response length
      fileSelectionAccuracy = Math.min(100, (filesSelected / 30) * 100); // Target 30 files

      if (filesSelected < 10) {
        recommendations.push(
          "⚠️ LOW FILE SELECTION: Less than 10 files selected, consider improving prompt"
        );
      }
      if (filesSelected > 35) {
        recommendations.push(
          "⚠️ HIGH FILE SELECTION: More than 35 files selected, may hit token limits"
        );
      }
      if (responseTokens < 500) {
        recommendations.push(
          "⚠️ SHORT RESPONSE: Response seems too brief, may indicate parsing issues"
        );
      }
    }

    return {
      tokenEfficiency,
      responseQuality,
      fileSelectionAccuracy,
      processingTime: this.calculateStepDuration("step2"),
      errorRate: step2Logs.some((log) => log.type.includes("Failed")) ? 100 : 0,
      recommendations,
    };
  }

  private analyzeStep3(): AnalysisMetrics {
    const step3Logs = this.logs.filter((log) => log.step === "step3");
    const inputLog = step3Logs.find((log) => log.type.includes("Input"));
    const responseLog = step3Logs.find((log) => log.type.includes("Response"));
    const parsedLog = step3Logs.find((log) => log.type.includes("Parsed"));

    const recommendations: string[] = [];
    let tokenEfficiency = 50;
    let responseQuality = 50;
    let fileSelectionAccuracy = 50;

    if (inputLog?.data) {
      const totalTokens = inputLog.data.totalPromptTokens || 0;
      const filesIncluded = inputLog.data.filesIncluded || 0;
      const maxTokens = 200000;

      tokenEfficiency = Math.max(0, 100 - (totalTokens / maxTokens) * 100);
      fileSelectionAccuracy = Math.min(100, (filesIncluded / 30) * 100); // Target 30 files

      if (totalTokens > 150000) {
        recommendations.push(
          "⚠️ HIGH TOKEN USAGE: Step 3 prompt exceeds 150k tokens"
        );
      }
      if (filesIncluded < 5) {
        recommendations.push(
          "⚠️ LOW FILE INCLUSION: Less than 5 files included in context"
        );
      }
    }

    if (responseLog?.data && parsedLog?.data) {
      const useCases = parsedLog.data.totalUseCases || 0;
      responseQuality = Math.min(100, (useCases / 8) * 100); // Target 8 use cases

      if (useCases < 5) {
        recommendations.push(
          "⚠️ INSUFFICIENT USE CASES: Less than 5 use cases generated"
        );
      }
      if (useCases > 12) {
        recommendations.push(
          "⚠️ TOO MANY USE CASES: More than 12 use cases may dilute focus"
        );
      }
    }

    return {
      tokenEfficiency,
      responseQuality,
      fileSelectionAccuracy,
      processingTime: this.calculateStepDuration("step3"),
      errorRate: step3Logs.some((log) => log.type.includes("Failed")) ? 100 : 0,
      recommendations,
    };
  }

  private analyzeSimulationGeneration(): AnalysisMetrics {
    const simLogs = this.logs.filter((log) =>
      log.type.includes("Simulation Generation")
    );
    const inputLog = simLogs.find((log) => log.type.includes("Request Input"));
    const responseLog = simLogs.find((log) =>
      log.type.includes("Raw LLM Response")
    );
    const successLog = simLogs.find((log) =>
      log.type.includes("Parsing Success")
    );

    const recommendations: string[] = [];
    let tokenEfficiency = 50;
    let responseQuality = 50;
    let fileSelectionAccuracy = 50;

    if (inputLog?.data) {
      const totalTokens = inputLog.data.totalPromptTokens || 0;
      const filesIncluded = inputLog.data.filesIncluded || 0;
      const maxTokens = 200000;

      tokenEfficiency = Math.max(0, 100 - (totalTokens / maxTokens) * 100);
      fileSelectionAccuracy = Math.min(100, (filesIncluded / 15) * 100); // Target 15 files
    }

    if (successLog?.data) {
      const steps = successLog.data.totalSteps || 0;
      const alternatingPattern = successLog.data.alternatingPattern;
      const avgLineRange = successLog.data.avgLineRange || 0;

      responseQuality = Math.min(
        100,
        (steps / 10) * 50 + (alternatingPattern ? 50 : 0)
      );

      if (!alternatingPattern) {
        recommendations.push(
          "❌ PATTERN VIOLATION: Steps don't follow node-edge alternating pattern"
        );
      }
      if (steps < 6) {
        recommendations.push(
          "⚠️ INSUFFICIENT STEPS: Less than 6 simulation steps generated"
        );
      }
      if (avgLineRange < 5) {
        recommendations.push(
          "⚠️ NARROW CODE MAPPING: Average line range is very small"
        );
      }
      if (avgLineRange > 100) {
        recommendations.push(
          "⚠️ BROAD CODE MAPPING: Average line range is very large, may lack precision"
        );
      }
    }

    return {
      tokenEfficiency,
      responseQuality,
      fileSelectionAccuracy,
      processingTime: this.calculateStepDuration("simulation"),
      errorRate: simLogs.some((log) => log.type.includes("Error")) ? 100 : 0,
      recommendations,
    };
  }

  private calculateStepDuration(step: string): number {
    const stepLogs = this.logs.filter(
      (log) => log.step === step || log.type.includes(step)
    );
    if (stepLogs.length < 2) return 0;

    const startTime = new Date(stepLogs[0].timestamp).getTime();
    const endTime = new Date(stepLogs[stepLogs.length - 1].timestamp).getTime();
    return endTime - startTime;
  }

  private calculateOverallScore(metrics: AnalysisMetrics[]): number {
    if (metrics.length === 0) return 0;

    const totalScore = metrics.reduce((sum, metric) => {
      return (
        sum +
        (metric.tokenEfficiency +
          metric.responseQuality +
          metric.fileSelectionAccuracy) /
          3
      );
    }, 0);

    return Math.round(totalScore / metrics.length);
  }

  generateMarkdownReport(): string {
    const workflowDuration = Date.now() - this.startTime;

    // Analyze each step
    const step1Analysis = this.analyzeStep1();
    const step2Analysis = this.analyzeStep2();
    const step3Analysis = this.analyzeStep3();
    const simulationAnalysis = this.analyzeSimulationGeneration();

    const allMetrics = [
      step1Analysis,
      step2Analysis,
      step3Analysis,
      simulationAnalysis,
    ];
    const overallScore = this.calculateOverallScore(allMetrics);

    let markdown = `# AI Workflow Analysis Report\n\n`;
    markdown += `**Workflow ID:** ${this.currentWorkflowId}\n`;
    markdown += `**Generated:** ${new Date().toISOString()}\n`;
    markdown += `**Total Duration:** ${workflowDuration}ms\n`;
    markdown += `**Overall Quality Score:** ${overallScore}/100\n\n`;

    // Executive Summary
    markdown += `## 📊 Executive Summary\n\n`;
    const allRecommendations = allMetrics.flatMap((m) => m.recommendations);
    const criticalIssues = allRecommendations.filter((r) =>
      r.includes("❌")
    ).length;
    const warnings = allRecommendations.filter((r) => r.includes("⚠️")).length;

    markdown += `- **Critical Issues:** ${criticalIssues}\n`;
    markdown += `- **Warnings:** ${warnings}\n`;
    markdown += `- **Steps Completed:** ${
      this.logs.filter((l) => l.type.includes("Completed")).length
    }\n`;
    markdown += `- **Error Rate:** ${
      allMetrics.reduce((sum, m) => sum + m.errorRate, 0) / allMetrics.length
    }%\n\n`;

    // Step-by-step analysis
    markdown += `## 🔍 Step-by-Step Analysis\n\n`;

    markdown += this.generateStepSection(
      "Step 1: Repository Tree Generation",
      step1Analysis,
      "step1"
    );
    markdown += this.generateStepSection(
      "Step 2: LLM File Selection",
      step2Analysis,
      "step2"
    );
    markdown += this.generateStepSection(
      "Step 3: Use Case Generation",
      step3Analysis,
      "step3"
    );
    markdown += this.generateStepSection(
      "Simulation Generation",
      simulationAnalysis,
      "simulation"
    );

    // Raw logs section
    markdown += `## 📋 Raw Logs\n\n`;
    markdown += `<details>\n<summary>Click to expand raw logs</summary>\n\n`;
    markdown += "```json\n";
    markdown += JSON.stringify(this.logs, null, 2);
    markdown += "\n```\n";
    markdown += `</details>\n\n`;

    // Performance metrics
    markdown += `## ⚡ Performance Metrics\n\n`;
    markdown += `| Step | Duration (ms) | Token Efficiency | Response Quality | File Selection |\n`;
    markdown += `|------|---------------|------------------|------------------|----------------|\n`;
    markdown += `| Step 1 | ${step1Analysis.processingTime} | ${step1Analysis.tokenEfficiency}% | ${step1Analysis.responseQuality}% | ${step1Analysis.fileSelectionAccuracy}% |\n`;
    markdown += `| Step 2 | ${step2Analysis.processingTime} | ${step2Analysis.tokenEfficiency}% | ${step2Analysis.responseQuality}% | ${step2Analysis.fileSelectionAccuracy}% |\n`;
    markdown += `| Step 3 | ${step3Analysis.processingTime} | ${step3Analysis.tokenEfficiency}% | ${step3Analysis.responseQuality}% | ${step3Analysis.fileSelectionAccuracy}% |\n`;
    markdown += `| Simulation | ${simulationAnalysis.processingTime} | ${simulationAnalysis.tokenEfficiency}% | ${simulationAnalysis.responseQuality}% | ${simulationAnalysis.fileSelectionAccuracy}% |\n\n`;

    // Recommendations
    markdown += `## 💡 Recommendations\n\n`;
    const uniqueRecommendations = [...new Set(allRecommendations)];
    uniqueRecommendations.forEach((rec) => {
      markdown += `- ${rec}\n`;
    });

    return markdown;
  }

  private generateStepSection(
    title: string,
    analysis: AnalysisMetrics,
    stepKey: string
  ): string {
    const stepLogs = this.logs.filter(
      (log) => log.step === stepKey || log.type.includes(stepKey)
    );

    let section = `### ${title}\n\n`;
    section += `**Duration:** ${analysis.processingTime}ms\n`;
    section += `**Quality Score:** ${Math.round(
      (analysis.tokenEfficiency +
        analysis.responseQuality +
        analysis.fileSelectionAccuracy) /
        3
    )}/100\n\n`;

    if (analysis.recommendations.length > 0) {
      section += `**Issues & Recommendations:**\n`;
      analysis.recommendations.forEach((rec) => {
        section += `- ${rec}\n`;
      });
      section += `\n`;
    }

    section += `<details>\n<summary>Detailed Logs</summary>\n\n`;

    stepLogs.forEach((log) => {
      section += `**${log.type}** (${log.timestamp})\n`;

      // Find stringified JSON in the log data
      const stringifiedJsonFields = this.findStringifiedJson(log.data);

      // Show raw data first
      section += "```json\n";
      section += this.formatContent(log.data);
      section += "\n```\n\n";

      // If we found stringified JSON fields, show parsed versions
      if (stringifiedJsonFields.length > 0) {
        section += `**📄 Parsed Content:**\n\n`;

        stringifiedJsonFields.forEach((parsedField) => {
          section += `**${parsedField.fieldName}:**\n`;

          if (parsedField.parseSuccessful) {
            section += "```json\n";
            section += this.formatContent(parsedField.parsedContent);
            section += "\n```\n\n";
          } else {
            section += `⚠️ **Parse Error:** ${parsedField.parseError}\n`;
            section += "```\n";
            section += this.formatContent(parsedField.rawContent, 1000); // Show first 1000 chars
            section += "\n```\n\n";
          }
        });
      }
    });

    section += `</details>\n\n`;

    return section;
  }

  finalizeWorkflow(): string {
    const report = this.generateMarkdownReport();

    const finalRawLogEntry = `DEBUGGING_AI_QUALITY: Workflow Finalized\n${JSON.stringify(
      {
        workflowId: this.currentWorkflowId,
        totalLogs: this.logs.length,
        reportLength: report.length,
      },
      null,
      2
    )}\n${"=".repeat(80)}\n`;

    this.rawLogs.push(finalRawLogEntry);

    // Store in global window for Stats for Nerds access
    if (typeof window !== "undefined") {
      window.aiWorkflowAnalysis = {
        report,
        logs: this.logs,
        workflowId: this.currentWorkflowId,
        timestamp: new Date().toISOString(),
      };
      // Maintain an array of analyses for multi-workflow support (e.g., onboarding vs simulation)
      if (typeof window !== "undefined") {
        const analysesKey = "aiWorkflowAnalyses" as const;
        if (!Array.isArray((window as any)[analysesKey])) {
          (window as any)[analysesKey] = [];
        }
        (window as any)[analysesKey].push({
          report,
          logs: this.logs,
          workflowId: this.currentWorkflowId,
          timestamp: new Date().toISOString(),
        });
      }
      window.aiWorkflowRawLogs = window.aiWorkflowRawLogs || [];
      window.aiWorkflowRawLogs.push(finalRawLogEntry);
    }

    logger.debug.info("DEBUGGING_AI_QUALITY: Workflow Finalized", {
      workflowId: this.currentWorkflowId,
      totalLogs: this.logs.length,
      reportLength: report.length,
    });

    return report;
  }

  // Method to get logs for external access
  getLogs(): AIWorkflowLogEntry[] {
    return [...this.logs];
  }

  // Method to clear logs
  clearLogs(): void {
    this.logs = [];
    this.rawLogs = [];
    if (typeof window !== "undefined") {
      window.aiWorkflowLogs = [];
      window.aiWorkflowRawLogs = [];
      window.aiWorkflowAnalysis = null;
    }
  }
}

// Global instance
export const aiWorkflowLogger = new AIWorkflowLogger();

// Extend Window interface for TypeScript support
declare global {
  interface Window {
    aiWorkflowLogs?: AIWorkflowLogEntry[];
    aiWorkflowRawLogs?: string[];
    aiWorkflowAnalysis?: {
      report: string;
      logs: AIWorkflowLogEntry[];
      workflowId: string;
      timestamp: string;
    };
  }
}
